import { 
  Component, 
  ElementRef, 
  OnInit, 
  ViewChild, 
  effect, 
  signal, 
  Injectable 
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { XYFlowModule } from 'ngx-xyflow';
import * as THREE from 'three';

// ==========================================
// 1. ESTADO COMPARTIDO (SERVICIO CON SIGNALS)
// ==========================================
@Injectable({
  providedIn: 'root'
})
export class ThreeStateService {
  // Signal reactivo para sincronizar xyflow con el canvas 3D
  cubeColor = signal<string>('#ff0055');
  cubeScale = signal<number>(1);
}

// ==========================================
// 2. COMPONENTE RENDERIZADOR DE THREE.JS
// ==========================================
@Component({
  selector: 'app-three-view',
  standalone: true,
  template: `<div #canvasTarget class="three-container"></div>`,
  styles: [`
    .three-container {
      width: 100%;
      height: 100%;
      background: #111;
      border-radius: 8px;
    }
  `]
})
export class ThreeViewComponent implements OnInit {
  @ViewChild('canvasTarget', { static: true }) canvasTarget!: ElementRef<HTMLDivElement>;

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private mesh!: THREE.Mesh;

  constructor(private state: ThreeStateService) {
    // Escucha reactiva a los Signals del servicio para mutar Three.js en vivo
    effect(() => {
      const color = this.state.cubeColor();
      const scale = this.state.cubeScale();

      if (this.mesh) {
        (this.mesh.material as THREE.MeshBasicMaterial).color.set(color);
        this.mesh.scale.set(scale, scale, scale);
      }
    });
  }

  ngOnInit() {
    this.initThree();
    this.animate();
  }

  private initThree() {
    const container = this.canvasTarget.nativeElement;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 100);
    this.camera.position.z = 5;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);

    const geometry = new THREE.BoxGeometry(1.5, 1.5, 1.5);
    const material = new THREE.MeshBasicMaterial({ color: this.state.cubeColor() });
    this.mesh = new THREE.Mesh(geometry, material);
    this.scene.add(this.mesh);

    // Ajustar el canvas si cambia de tamaño el contenedor
    const resizeObserver = new ResizeObserver(() => {
      this.camera.aspect = container.clientWidth / container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(container.clientWidth, container.clientHeight);
    });
    resizeObserver.observe(container);
  }

  private animate() {
    requestAnimationFrame(() => this.animate());
    if (this.mesh) {
      this.mesh.rotation.x += 0.01;
      this.mesh.rotation.y += 0.01;
    }
    this.renderer.render(this.scene, this.camera);
  }
}

// ==========================================
// 3. COMPONENTE PRINCIPAL (XYFLOW + DASHBOARD)
// ==========================================
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule, XYFlowModule, ThreeViewComponent],
  template: `
    <div class="workspace">
      <!-- Panel de xyflow -->
      <div class="flow-panel">
        <ngx-xyflow [(nodes)]="nodes" [(edges)]="edges" [fitView]="true">
          
          <!-- Nodo Personalizado: Controlador de Color -->
          <ngx-xyflow-node nodeType="colorController">
            <ng-template let-data>
              <div class="custom-node color-node">
                <h4>Color Control</h4>
                <p>ID Objeto: {{data.targetId}}</p>
                <input 
                  type="color" 
                  [ngModel]="state.cubeColor()" 
                  (ngModelChange)="onColorChange($event)"
                />
                <!-- Handles/Conectores nativos de xyflow -->
                <div class="xyflow-handle target-handle" style="top: 50%; left: -4px;"></div>
                <div class="xyflow-handle source-handle" style="top: 50%; right: -4px;"></div>
              </div>
            </ng-template>
          </ngx-xyflow-node>

          <!-- Nodo Personalizado: Controlador de Escala -->
          <ngx-xyflow-node nodeType="scaleController">
            <ng-template let-data>
              <div class="custom-node scale-node">
                <h4>Scale Control</h4>
                <input 
                  type="range" 
                  min="0.5" 
                  max="2.5" 
                  step="0.1" 
                  [ngModel]="state.cubeScale()" 
                  (ngModelChange)="onScaleChange($event)"
                />
                <span>{{ state.cubeScale() }}x</span>
                <div class="xyflow-handle source-handle" style="top: 50%; right: -4px;"></div>
              </div>
            </ng-template>
          </ngx-xyflow-node>

        </ngx-xyflow>
      </div>

      <!-- Panel de Renderizado 3D -->
      <div class="preview-panel">
        <div class="panel-header">Canvas de Visualización (Three.js)</div>
        <div class="canvas-wrapper">
          <app-three-view></app-three-view>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .workspace {
      display: flex;
      width: 100vw;
      height: 100vh;
      font-family: sans-serif;
      background: #1e1e24;
      color: #fff;
    }
    .flow-panel {
      flex: 7;
      height: 100%;
      background: #141416;
    }
    .preview-panel {
      flex: 3;
      display: flex;
      flex-direction: column;
      background: #232329;
      border-left: 2px solid #2d2d35;
      padding: 15px;
    }
    .panel-header {
      font-weight: bold;
      margin-bottom: 10px;
      color: #aaa;
    }
    .canvas-wrapper {
      flex: 1;
      overflow: hidden;
    }
    /* Estilos para los Nodos de xyflow */
    .custom-node {
      background: #2b2b36;
      border: 2px solid #4e4e61;
      border-radius: 8px;
      padding: 12px;
      color: white;
      min-width: 150px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.3);
    }
    .custom-node h4 { margin: 0 0 8px 0; font-size: 14px; color: #00ffcc; }
    .custom-node p { margin: 0 0 10px 0; font-size: 11px; color: #888; }
    .custom-node input[type="color"] {
      width: 100%;
      height: 30px;
      border: none;
      cursor: pointer;
      background: transparent;
    }
    .custom-node input[type="range"] { width: 100%; }
    .xyflow-handle {
      position: absolute;
      width: 8px;
      height: 8px;
      background: #00ffcc;
      border-radius: 50%;
    }
  `]
})
export class AppComponent {
  // Inicialización de la gráfica de xyflow usando tipado estructural nativo
  nodes = [
    {
      id: 'node-1',
      type: 'scaleController',
      position: { x: 50, y: 150 },
      data: { label: 'Scale Slider' }
    },
    {
      id: 'node-2',
      type: 'colorController',
      position: { x: 300, y: 100 },
      data: { targetId: 'Mesh_Cube_01' }
    }
  ];

  edges = [
    { id: 'edge-1', source: 'node-1', target: 'node-2' }
  ];

  constructor(public state: ThreeStateService) {}

  onColorChange(newColor: string) {
    this.state.cubeColor.set(newColor);
  }

  onScaleChange(newScale: number) {
    this.state.cubeScale.set(Number(newScale));
  }
}